'use client';

import { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import BookingExtraTeaser from '@/components/modules/booking-extra-teaser/booking-extra-teaser';
import SeatSelectionModal from '@/components/modules/seat-selection-modal/seat-selection-modal';
import { getSeatNameByPassengerHash } from '@/components/modules/seat-selection-modal/seat-selection-modal-utils/seat-selection-modal-utils';
import UserAvatar, {
  BasicPerson,
} from '@/components/molecules/user-avatar/user-avatar';
import ModuleWrapper from '@/components/utilities/module-wrapper/module-wrapper';
import useQuerySeatSelection from '@/integrations/content/content-modules/seat-selection/seat-selection.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import styles from './seat-selection.module.scss';

export interface SeatSelectionProps {
  /** The UUID of the module */
  id: Content.Components['TransportSeatingHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['TransportSeatingHeadlessModuleDto']['pageId'];
  /** Booking parameters */
  parameters: Content.Components['TransportSeatingHeadlessModuleDto']['parameters'];
  /** Module settings*/
  settings: Content.Components['TransportSeatingHeadlessModuleDto']['settings'];
}

const SeatSelection = ({
  id,
  pageId,
  parameters,
  settings,
}: SeatSelectionProps) => {
  const { t } = useI18n();
  const [showSeatSelectionModal, setShowSeatSelectionModal] = useState(false);
  const { isInWebview } = usePageDetailsContext();

  const identifiers = {
    bookingStateId: parameters.bookingStateId,
    id,
    pageId,
  };

  const {
    data: dataSeatSelection,
    isFetching: isFetchingSeatSelection,
    isLoading: isLoadingSeatSelection,
  } = useQuerySeatSelection({
    parameters: identifiers,
  });

  const hasNoData = !isFetchingSeatSelection && !dataSeatSelection;

  const getBasicPerson = (
    passenger: Content.Properties.TransportPassenger,
  ): BasicPerson => {
    const { firstName, lastName, placeholders } = passenger;

    if (!firstName && !lastName) {
      return {
        firstName: placeholders.firstName,
        lastName: placeholders.lastName,
      };
    }

    return { firstName, lastName };
  };

  const getNameDisplay = (
    passenger: Content.Properties.TransportPassenger,
  ): string => {
    const { firstName, lastName, placeholders } = passenger;

    if (!firstName && !lastName) {
      return `${placeholders.firstName} ${placeholders.lastName}: `;
    }

    return `${firstName} ${lastName}: `;
  };

  if (hasNoData) return null;

  const handleOnClickButton = () => {
    setShowSeatSelectionModal(true);
  };

  const handleOnClickEditLink = () => {
    setShowSeatSelectionModal(true);
  };

  if (isInWebview) {
    return (
      <SeatSelectionModal
        headline={dataSeatSelection?.headline}
        identifiers={identifiers}
        isInWebview={isInWebview}
        isOpen
        onClose={() => {
          setShowSeatSelectionModal(false);
        }}
        transportSeatings={dataSeatSelection?.transportSeatings}
      />
    );
  }

  return (
    <ModuleWrapper
      className={styles.component}
      id={id}
      padding="none"
      settings={settings}
    >
      <BookingExtraTeaser
        isFetching={isFetchingSeatSelection}
        isLoading={isLoadingSeatSelection}
        {...dataSeatSelection}
        onClickButton={() => handleOnClickButton()}
        onClickEditLink={() => handleOnClickEditLink()}
      >
        {!isLoadingSeatSelection && isFetchingSeatSelection ? (
          <SkeletonLoader borderRadius="4px" height="35px" width="100%" />
        ) : (
          <div className={styles.transportationDetailsWrapper}>
            {dataSeatSelection?.transportSeatings
              .filter(transportSeating =>
                transportSeating.passengers.some(
                  passenger =>
                    passenger.selectedSeats &&
                    passenger.selectedSeats.length > 0,
                ),
              )
              .map(transportSeating => (
                <div
                  key={transportSeating.headline}
                  className={styles.transportationDetailsItem}
                >
                  <div className={styles.transportationNameWrapper}>
                    <Icon
                      name={
                        transportSeating.travelDirection === 'outbound'
                          ? 'PlaneTakeOff'
                          : 'PlaneLanding'
                      }
                      size={16}
                    />

                    <Typography tagStyle="bodyMediumBold">
                      {transportSeating.headline}
                    </Typography>
                  </div>

                  <div className={styles.passengersWrapper}>
                    {transportSeating.passengers.map(passenger => (
                      <div
                        key={passenger.uniquePaxHash}
                        className={styles.passengerItem}
                      >
                        <UserAvatar passenger={getBasicPerson(passenger)} />

                        <Typography tagStyle="bodyMedium">
                          {getNameDisplay(passenger)}

                          <Typography tagStyle="bodyMediumBold">
                            {getSeatNameByPassengerHash(
                              passenger.uniquePaxHash,
                              transportSeating,
                            ) ?? t('items.modules.bookingFlow.noneSelected')}
                          </Typography>
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </BookingExtraTeaser>

      <SeatSelectionModal
        headline={dataSeatSelection?.headline}
        identifiers={identifiers}
        isOpen={showSeatSelectionModal}
        onClose={() => {
          setShowSeatSelectionModal(false);
        }}
        transportSeatings={dataSeatSelection?.transportSeatings}
      />
    </ModuleWrapper>
  );
};

SeatSelection.displayName = 'SeatSelection';

export default SeatSelection;
